import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';

import { UnsubscribeService } from '@webgate/angular-azure-core';
import { ManualTriggerResponse, ManualTriggersService } from './manual-triggers.service';
import { MigrationResult, NotesAzureUserMapping } from '../../../models/notesToAzureUserMigration';
import { ExportService } from '../../../services/exportService';
import { ConfirmDialogComponent } from './confirm-dialog.component';
import { ManualGraphMatchesComponent } from './manual-graph-matches.component';
import { GraphMatchOptions, NameMatchingStrategy } from '../../../models/manualGraphMatches';

@Component({
  selector: 'app-admin-manual-triggers',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSnackBarModule,
    MatTabsModule,
    MatDialogModule,
    MatTooltipModule,
    MatSelectModule,
    ManualGraphMatchesComponent
  ],
  templateUrl: './admin-manual-triggers.component.html',
  styleUrl: './admin-manual-triggers.component.scss'
})
export class AdminManualTriggersComponent {
  private manualTriggersService = inject(ManualTriggersService);
  private snackBar = inject(MatSnackBar);
  private unsubscribeService = inject(UnsubscribeService);
  private exportService = inject(ExportService);
  private dialog = inject(MatDialog);

  isLoadingOrganizations = false;
  isLoadingRecommendationTitles = false;
  isLoadingUserDisplayNames = false;
  isLoadingInvalidUserIds = false;
  isLoadingInvalidUserIdsStatistics = false;
  isLoadingInvalidUserIdsWithMatches = false;
  isApplyingGraphMatches = false;
  isLoadingRecommendationStateUpdate = false;
  userMappingJson = '[]';

  // Graph Match Options
  graphMatchOptions: GraphMatchOptions = {
    nameMatchingStrategy: NameMatchingStrategy.Normalized,
    emailDomainFilter: ''
  };

  readonly exampleJson = `[
  {
    "azureId": "12345678-1234-1234-1234-123456789012",
    "notesName": "Max Mustermann"
  },
  {
    "azureId": "87654321-4321-4321-4321-210987654321",
    "notesName": "Anna Schmidt"
  }
]`;

  copyExampleToClipboard(): void {
    navigator.clipboard
      .writeText(this.exampleJson)
      .then(() => {
        this.snackBar.open('Beispiel in Zwischenablage kopiert!', 'Schließen', {
          duration: 2000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: 'success-snackbar'
        });
      })
      .catch((err) => {
        console.error('Fehler beim Kopieren:', err);
        this.snackBar.open('Fehler beim Kopieren in die Zwischenablage', 'Schließen', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: 'error-snackbar'
        });
      });
  }

  triggerOrganizationImport(): void {
    this.isLoadingOrganizations = true;

    this.manualTriggersService
      .triggerOrganizationImport()
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: (response: ManualTriggerResponse) => {
          this.isLoadingOrganizations = false;
          this.snackBar.open('Organization import executed successfully!', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'success-snackbar'
          });
          console.log('Organization import response:', response);
        },
        error: (error: any) => {
          this.isLoadingOrganizations = false;
          const errorMessage = this.extractErrorMessage(error);
          this.snackBar.open(errorMessage, 'Close', {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'error-snackbar'
          });
          console.error('Organization import error:', error);
        }
      });
  }

  triggerRecommendationTitleMigration(): void {
    this.isLoadingRecommendationTitles = true;

    this.manualTriggersService
      .triggerRecommendationTitleMigration()
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: (response: ManualTriggerResponse) => {
          this.isLoadingRecommendationTitles = false;

          let message = 'Recommendation titles migrated successfully!';
          if (response.statistics) {
            message = `Migration completed: ${response.statistics.successCount} updated, ${
              response.statistics.failureCount
            } failed (${response.statistics.durationSeconds.toFixed(1)}s)`;
          }

          this.snackBar.open(message, 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'success-snackbar'
          });
          console.log('Recommendation title migration response:', response);
        },
        error: (error: any) => {
          this.isLoadingRecommendationTitles = false;
          const errorMessage = this.extractErrorMessage(error);
          this.snackBar.open(errorMessage, 'Close', {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'error-snackbar'
          });
          console.error('Recommendation title migration error:', error);
        }
      });
  }

  triggerUserDisplayNameMigration(): void {
    let mappings: NotesAzureUserMapping[] = [];

    // Parse JSON-Eingabe
    try {
      if (this.userMappingJson && this.userMappingJson.trim()) {
        mappings = JSON.parse(this.userMappingJson);

        // Validiere, dass es ein Array ist
        if (!Array.isArray(mappings)) {
          this.snackBar.open('Ungültiges JSON: Es muss ein Array sein', 'Schließen', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'error-snackbar'
          });
          return;
        }
      }
    } catch (error) {
      this.snackBar.open(
        'Fehler beim Parsen des JSON: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'),
        'Schließen',
        {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: 'error-snackbar'
        }
      );
      console.error('JSON parse error:', error);
      return;
    }

    // Bestätigungsdialog anzeigen
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        title: 'User Display-Namen aktualisieren',
        message: `Möchten Sie die User Display-Namen Migration wirklich starten? Es werden ${mappings.length} Mappings verarbeitet. Diese Aktion kann nicht rückgängig gemacht werden.`,
        confirmText: 'Ja, Migration starten',
        cancelText: 'Abbrechen'
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.executeUserDisplayNameMigration(mappings);
      }
    });
  }

  private executeUserDisplayNameMigration(mappings: NotesAzureUserMapping[]): void {
    this.isLoadingUserDisplayNames = true;

    this.manualTriggersService
      .triggerUserDisplayNameMigration(mappings)
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: (result: MigrationResult) => {
          this.isLoadingUserDisplayNames = false;

          const message = `Migration abgeschlossen: ${result.updatedRecommendations}/${result.totalRecommendations} Recommendations aktualisiert, ${result.updatedAudits}/${result.totalAudits} Audits aktualisiert`;

          this.snackBar.open(message, 'Schließen', {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'success-snackbar'
          });
          console.log('User display name migration result:', result);
        },
        error: (error: any) => {
          this.isLoadingUserDisplayNames = false;
          const errorMessage = this.extractErrorMessage(error);
          this.snackBar.open(errorMessage, 'Schließen', {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'error-snackbar'
          });
          console.error('User display name migration error:', error);
        }
      });
  }

  applyGraphMatches(): void {
    // Bestätigungsdialog anzeigen
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        title: 'Graph API Matches automatisch anwenden',
        message:
          'Möchten Sie die automatische Anwendung von Graph API Matches wirklich starten? Es werden alle eindeutigen Matches (MatchCount = 1) automatisch auf ungültige User-IDs angewendet. Diese Aktion kann nicht rückgängig gemacht werden.',
        confirmText: 'Ja, Matches anwenden',
        cancelText: 'Abbrechen'
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.executeApplyGraphMatches();
      }
    });
  }

  private executeApplyGraphMatches(): void {
    this.isApplyingGraphMatches = true;

    const options: GraphMatchOptions = {
      nameMatchingStrategy: this.graphMatchOptions.nameMatchingStrategy,
      emailDomainFilter: this.graphMatchOptions.emailDomainFilter?.trim() || undefined
    };

    this.manualTriggersService
      .applyGraphMatches(options)
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: (result: MigrationResult) => {
          this.isApplyingGraphMatches = false;

          const message = `Graph Matches angewendet: ${result.updatedRecommendations}/${result.totalRecommendations} Recommendations aktualisiert, ${result.updatedAudits}/${result.totalAudits} Audits aktualisiert`;

          this.snackBar.open(message, 'Schließen', {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'success-snackbar'
          });
          console.log('Graph matches application result:', result);
        },
        error: (error: any) => {
          this.isApplyingGraphMatches = false;
          const errorMessage = this.extractErrorMessage(error);
          this.snackBar.open(errorMessage, 'Schließen', {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'error-snackbar'
          });
          console.error('Graph matches application error:', error);
        }
      });
  }

  exportInvalidUserIds(): void {
    this.isLoadingInvalidUserIds = true;

    this.manualTriggersService
      .exportInvalidUserIds()
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: (blob: Blob) => {
          this.isLoadingInvalidUserIds = false;

          // Extrahiere Dateiname aus Content-Disposition Header oder verwende Standard
          const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
          const fileName = `invalid-user-ids-${timestamp}.xlsx`;

          this.exportService.createFileDownloadFromBlob(blob, fileName);

          this.snackBar.open('Excel-Export erfolgreich heruntergeladen!', 'Schließen', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'success-snackbar'
          });
        },
        error: (error: any) => {
          this.isLoadingInvalidUserIds = false;
          const errorMessage = this.extractErrorMessage(error);
          this.snackBar.open(errorMessage, 'Schließen', {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'error-snackbar'
          });
          console.error('Invalid user IDs export error:', error);
        }
      });
  }

  exportInvalidUserIdsStatistics(): void {
    this.isLoadingInvalidUserIdsStatistics = true;

    this.manualTriggersService
      .exportInvalidUserIdsStatistics()
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: (blob: Blob) => {
          this.isLoadingInvalidUserIdsStatistics = false;

          const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
          const fileName = `invalid-user-ids-statistics-${timestamp}.xlsx`;

          this.exportService.createFileDownloadFromBlob(blob, fileName);

          this.snackBar.open('Statistik-Export erfolgreich heruntergeladen!', 'Schließen', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'success-snackbar'
          });
        },
        error: (error: any) => {
          this.isLoadingInvalidUserIdsStatistics = false;
          const errorMessage = this.extractErrorMessage(error);
          this.snackBar.open(errorMessage, 'Schließen', {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'error-snackbar'
          });
          console.error('Invalid user IDs statistics export error:', error);
        }
      });
  }

  exportInvalidUserIdsWithMatches(): void {
    this.isLoadingInvalidUserIdsWithMatches = true;

    const options: GraphMatchOptions = {
      nameMatchingStrategy: this.graphMatchOptions.nameMatchingStrategy,
      emailDomainFilter: this.graphMatchOptions.emailDomainFilter?.trim() || undefined
    };

    this.manualTriggersService
      .exportInvalidUserIdsWithMatches(options)
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: (blob: Blob) => {
          this.isLoadingInvalidUserIdsWithMatches = false;

          const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
          const fileName = `invalid-user-ids-with-matches-${timestamp}.xlsx`;

          this.exportService.createFileDownloadFromBlob(blob, fileName);

          this.snackBar.open('Match-Export erfolgreich heruntergeladen!', 'Schließen', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'success-snackbar'
          });
        },
        error: (error: any) => {
          this.isLoadingInvalidUserIdsWithMatches = false;
          const errorMessage = this.extractErrorMessage(error);
          this.snackBar.open(errorMessage, 'Schließen', {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'error-snackbar'
          });
          console.error('Invalid user IDs with matches export error:', error);
        }
      });
  }

  triggerRecommendationStateUpdate(): void {
    // Bestätigungsdialog anzeigen
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        title: 'Recommendation Status Update',
        message:
          'Möchten Sie die manuelle Aktualisierung der Recommendation-Status wirklich starten? Diese Aktion aktualisiert alle pending Recommendations basierend auf ihren Deadlines und sendet Reminder-E-Mails.',
        confirmText: 'Ja, Update starten',
        cancelText: 'Abbrechen'
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.executeRecommendationStateUpdate();
      }
    });
  }

  private executeRecommendationStateUpdate(): void {
    this.isLoadingRecommendationStateUpdate = true;

    this.manualTriggersService
      .triggerRecommendationStateUpdate()
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: (response: ManualTriggerResponse) => {
          this.isLoadingRecommendationStateUpdate = false;
          this.snackBar.open('Recommendation status update executed successfully!', 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'success-snackbar'
          });
          console.log('Recommendation state update response:', response);
        },
        error: (error: any) => {
          this.isLoadingRecommendationStateUpdate = false;
          const errorMessage = this.extractErrorMessage(error);
          this.snackBar.open(errorMessage, 'Close', {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'error-snackbar'
          });
          console.error('Recommendation state update error:', error);
        }
      });
  }

  private extractErrorMessage(error: any): string {
    // Handle HTTP error responses
    if (error?.error) {
      // If backend returns structured error
      if (typeof error.error === 'string') {
        return `Error: ${error.error}`;
      }
      // If backend returns JSON error
      if (error.error.message) {
        return `Error: ${error.error.message}`;
      }
      if (error.error.title) {
        return `Error: ${error.error.title}`;
      }
    }

    // Handle HTTP status codes
    if (error?.status) {
      switch (error.status) {
        case 0:
          return 'Network error: Unable to connect to server. Please check your connection.';
        case 400:
          return 'Bad request: The server could not understand the request.';
        case 401:
          return 'Unauthorized: Please log in again.';
        case 403:
          return 'Forbidden: You do not have permission to perform this action.';
        case 404:
          return 'Not found: The requested endpoint does not exist.';
        case 500:
          return 'Server error: An internal error occurred. Please try again later or contact IT support.';
        case 502:
          return 'Bad gateway: The server is temporarily unavailable.';
        case 503:
          return 'Service unavailable: The server is temporarily down for maintenance.';
        default:
          return `Error ${error.status}: ${error.statusText || 'An unexpected error occurred'}`;
      }
    }

    // Handle generic errors
    if (error?.message) {
      return `Error: ${error.message}`;
    }

    // Fallback
    return 'An unexpected error occurred. Please try again or contact IT support.';
  }
}
